/**
 * TRAC-1188: per-browser override for where a dev handoff goes (LivePerson or the
 * Amazon Connect proof of concept). Set from Dev Settings, stored in localStorage,
 * and sent as X-Handoff-Provider on every request built by getRequestHeaders(), so
 * escalate, live-chat-availability and the handoff calls all agree.
 *
 * The backend is the boundary: it honors the header only where Connect is allowed
 * (dev/local). The frontend still never sends it from a prod build, and only sends
 * a known value.
 */

export const HANDOFF_PROVIDER_STORAGE_KEY = 'traci_dev_handoff_provider';
export const HANDOFF_PROVIDER_HEADER = 'X-Handoff-Provider';

export type HandoffProvider = 'liveperson' | 'connect';

const PROVIDERS: readonly HandoffProvider[] = ['liveperson', 'connect'];

export const HANDOFF_PROVIDER_LABELS: Record<HandoffProvider, string> = {
  liveperson: 'LivePerson',
  connect: 'Connect POC',
};

export function isHandoffProvider(value: unknown): value is HandoffProvider {
  return typeof value === 'string' && (PROVIDERS as readonly string[]).includes(value);
}

/** Read at call time (not module load) so a test can stub VITE_ENV. */
export function isProdBuild(): boolean {
  return import.meta.env.VITE_ENV === 'prod';
}

/** The stored override, or null when unset or not a known provider. */
export function getHandoffProviderOverride(): HandoffProvider | null {
  try {
    const stored = localStorage.getItem(HANDOFF_PROVIDER_STORAGE_KEY);
    return isHandoffProvider(stored) ? stored : null;
  } catch {
    return null;
  }
}

/** null clears the override (follow the global default). */
export function setHandoffProviderOverride(value: HandoffProvider | null): void {
  try {
    if (value === null) {
      localStorage.removeItem(HANDOFF_PROVIDER_STORAGE_KEY);
    } else {
      localStorage.setItem(HANDOFF_PROVIDER_STORAGE_KEY, value);
    }
  } catch { /* storage unavailable: the override just doesn't stick */ }
}

/** The header to merge into a request; empty in a prod build or with no override. */
export function handoffProviderHeaders(): Record<string, string> {
  if (isProdBuild()) return {};
  const provider = getHandoffProviderOverride();
  return provider ? { [HANDOFF_PROVIDER_HEADER]: provider } : {};
}
